import { Component, computed, HostListener, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatIconModule } from "@angular/material/icon";
import { WorkflowOverviewService } from "../home/data-access/workflow-overview.service";
import { LoaderService } from "@shared-utils/loader/loader.service";
import { ProcessService } from "@shared-api/process/process.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter, map } from "rxjs";
import { ProcessResponse } from "@shared-interfaces/shared-process";
import { Contract, ContractAnalysisResult, FilterState, NewContractAnalysisResult, Specifications } from "@shared-interfaces/shared-workflow";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { MatButton, MatIconButton } from "@angular/material/button";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ChipComponent } from "./ui/chip/chip.component";
import { ContractStatus } from "@shared-types/contract-status";
import { MatExpansionModule, MatExpansionPanel, MatExpansionPanelHeader, MatAccordion } from "@angular/material/expansion";
import { AnalysisResultItemComponent } from "./ui/analysis-result-item/analysis-result-item.component";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { ContractNameDialogComponent } from "./ui/contract-name-dialog/contract-name-dialog.component";
import { SubscriptionOverviewService } from "../home/data-access/subscription-overview.service";
import { KeyValuePipe, AsyncPipe, DatePipe } from '@angular/common';
import { UploadComponent } from "@shared-features/upload/upload.component";
import { FileData, WorkflowType } from "@shared-types/shared-file";
import { UploadedFileInformation } from "@shared-types/shared-upload";
import { allSameValue, atLeastOneValue } from "@shared-helper/helper-functions";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PromptSet, PromptSetApiService, PromptSetListQuery, PromptAccessLevel, OutputSchema } from '../administration/shared/api.service';
import { effect, signal, untracked } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContentCollectionService } from '@shared-api/content-collection/content-collection.service';
import { AnalysisResult } from '@shared-interfaces/shared-workflow';
import { HttpClient } from '@angular/common/http';
import { buildActiveStepNumber, classifySummaryAnswer, countQuestionVerdicts } from './classify-summary-answer';
import { environment } from '@environments/environment';

@Component({
    selector: 'contract',
    standalone: true,
    imports: [
        MatIconModule,
        MatButton,
        MatSelectModule,
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconButton,
        ChipComponent,
        MatExpansionModule,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        AnalysisResultItemComponent,
        RouterLink,
        AsyncPipe,
        DatePipe,
        UploadComponent,
        TranslateModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatTooltipModule
    ],
    providers: [
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic',
                appearance: 'outline'
            }
        }
    ],
    templateUrl: './contract.component.html',
    styleUrl: './contract.component.scss'
})
export class ContractComponent implements OnDestroy {

    private readonly DISCLAIMER_TEXT = 'Disclaimer: Neither Deloitte Consulting nor the AI Tool provide legal services. The services rendered by the AI Tool are intended solely for initial screening and comparison purposes.';

    /**
     * TEMPORARY / DEV-ONLY DEMO FEATURE.
     * Hardcoded legend (7 Kriterien + 19 S-Kategorien) requested by Max Puetz for the
     * "Test IKT-Scanner" prompt set, to be shown in the Summary sheet for a client demo.
     * This is intentionally hardcoded and gated to non-production environments only —
     * NOT a long-term/dynamic solution. See email thread "IKT-Scanner Prompt" (Jul 2026).
     * TODO: Replace with a proper admin-configurable legend setting if this becomes a
     * recurring requirement across prompt sets (estimated 2-3 days, FE + BE).
     */
    private readonly IKT_LEGEND_PROMPT_SET_NAME = 'Test IKT-Scanner';

    private readonly IKT_KRITERIEN: { title: string; explanation: string }[] = [
        {
            title: 'Kriterium 1 – Liegt eine Dienstleistung vor?',
            explanation: 'Die zu prüfende Leistung muss ein Dauerschuldverhältnis darstellen und über eine einmalige Lieferung oder punktuelle Handlung hinausgehen. Keine Dienstleistung im Sinne der DORA sind: reine Lieferungen von Produkten oder Hardware, einmalige Beratungsleistungen, der isolierte Erwerb von Softwarelizenzen ohne begleitende Leistungen sowie Vorgänge ohne fortlaufende Tätigkeitspflichten. Eine Dienstleistung liegt vor, wenn der Anbieter kontinuierliche Tätigkeiten, Support-, Betriebs-, Wartungs- oder sonstige laufende Leistungen erbringt, unabhängig von der formalen Vertragsbezeichnung. Maßgeblich ist allein der tatsächliche Leistungsinhalt.'
        },
        {
            title: 'Kriterium 2 – Handelt es sich um einen digitalen Dienst oder Datendienst?',
            explanation: 'Die Digitalität muss das prägende Element der vertraglich geschuldeten Leistung sein. Digitale oder datenbasierte Prozesse müssen explizit Bestandteil des Leistungsgegenstands sein. Nicht ausreichend sind Leistungen, die lediglich mittelbar digitale Mittel nutzen (z. B. Kommunikation per E-Mail) oder deren digitale Elemente nicht vertraglich geschuldet sind. Relevante digitale Dienste umfassen u. a. Softwarebereitstellung, Datenverarbeitung, Datentransfer, Analyseleistungen, Schnittstellenleistungen oder digitale Plattformdienste. Entscheidend ist: Nur der tatsächlich vereinbarte digitale Teil ist für die DORA-Prüfung maßgeblich.'
        },
        {
            title: 'Kriterium 3 – Erfolgt die Bereitstellung über IKT-Systeme?',
            explanation: 'Die digitale Dienstleistung muss über Systeme der Informations- und Kommunikationstechnik bereitgestellt werden. Dies umfasst jede Form elektronischer oder digitaler Datenverarbeitung – unabhängig davon, ob sie über lokale Systeme, Hosting, Rechenzentren oder Cloud-Infrastrukturen erfolgt. Bei digitalen Diensten ist dieses Kriterium regelmäßig automatisch erfüllt. Es muss formal geprüft werden, stellt aber selten ein eigenständiges Abgrenzungsmerkmal dar.'
        },
        {
            title: 'Kriterium 4 – Erfolgt die Bereitstellung für interne oder externe Nutzer des Finanzunternehmens?',
            explanation: 'IKT-Dienstleistungen müssen einem oder mehreren Nutzern des Finanzunternehmens bereitgestellt werden. Nutzer können interne Beschäftigte, Fachbereiche oder externe Nutzer sein, sofern die Leistung im Rahmen der Geschäftstätigkeit des Instituts zur Verfügung gestellt wird. Nicht umfasst sind Leistungen, die ausschließlich für Endkunden des Dienstleisters oder für nicht regulierte Dritte bestimmt sind, sowie Leistungen, die das Finanzunternehmen lediglich vertreibt.'
        },
        {
            title: 'Kriterium 5 – Erfolgt die Bereitstellung dauerhaft?',
            explanation: 'Die Leistung muss auf eine fortlaufende, wiederkehrende oder langfristig angelegte Erbringung ausgelegt sein. Als Indiz gilt insbesondere eine Vertragslaufzeit von über 12 Monaten, wobei auch jährlich durchgeführte Leistungen als dauerhaft gelten. Einmalige Tätigkeiten oder zeitlich eng begrenzte Einzelprojekte sind nicht dauerhaft. Entscheidend ist die inhaltliche, nicht die formale Dauerhaftigkeit.'
        },
        {
            title: 'Kriterium 6 – Wird die Leistung für die Ausübung der Geschäftstätigkeit benötigt?',
            explanation: 'Die Leistung muss einen konkreten Bezug zur Geschäftstätigkeit des Finanzunternehmens haben und Geschäftsprozesse unterstützen, beeinflussen oder ermöglichen. Es genügt, wenn die Leistung in einem Zusammenhang mit Prozessen des Instituts steht, auch wenn keine unmittelbare Relevanz für regulierte Bankgeschäfte besteht. Nicht umfasst sind rein optionale, nicht prozessrelevante Zusatzfunktionen („Nice-to-have"). Die BaFin betont, dass dieses Kriterium risikoorientiert zu berücksichtigen ist.'
        },
        {
            title: 'Kriterium 7 – Wird die Dienstleistung von einem Drittdienstleister bereitgestellt?',
            explanation: 'Die Leistung muss von einem externen Anbieter stammen, der nicht Teil des Finanzunternehmens ist. Ausnahmen gelten für: öffentliche Verwaltungen und Aufsichtsbehörden (soweit gesetzliche Aufgaben); Verbände (z. B. DSGV, Regionalverbände), soweit satzungsmäßige Verbandsaufgaben; beaufsichtigte Finanzunternehmen, deren digitale Leistungen in unmittelbarem Zusammenhang mit regulierten Finanzdienstleistungen stehen (gemäß ESA Q&A 2999). Leistungen konzerninterner Gesellschaften sind grundsätzlich einzubeziehen, sofern keine der genannten Ausnahmen greift.'
        },
    ];

    private readonly IKT_S_KATEGORIEN: { category: string; description: string }[] = [
        { category: 'S01 - IKT-Projektmanagement', description: 'Projektmanagementleistungen mit direktem Bezug zu IKT-Implementierung, Änderungen oder Einführung von IKT-Assets oder IKT-Prozessen. Restriktiv verwenden.' },
        { category: 'S02 - IKT-Entwicklung', description: 'Entwicklung, Anpassung oder Testen von Software, Individualprogrammierung, Customizing, Anforderungsanalyse, technische Fehlerbehebung. Restriktiv verwenden.' },
        { category: 'S03 - IKT-Helpdesk / First-Level-Support', description: 'Support bei IKT-Vorfällen für Nutzer (Helpdesk, First Level). Reine Hotlines ohne tieferen technischen Bezug zählen nicht.' },
        { category: 'S04 - IKT-Sicherheitsmanagement', description: 'Firewall-Management, Vorfallsbehandlung, Forensik, SIEM, SOC, Penetrationstests, Schwachstellenscans, CERT-Leistungen. Restriktiv verwenden.' },
        { category: 'S05 - Bereitstellung von Daten', description: 'Digitale Bereitstellung vertraglich vereinbarter Daten für Geschäftsprozesse (z. B. Ratings, Bonitätsdaten, Marktinformationen mit operativer Relevanz).' },
        { category: 'S06 - Datenanalyse', description: 'Digitale Analysen von Institutsdaten über Schnittstellen; Rückgabe digitaler Auswertungen (z. B. Big Data, Kampagnenanalysen, Segmentierungen).' },
        { category: 'S07 - IKT-Einrichtungen & Hosting (ohne Cloud)', description: 'Bereitstellung oder Betrieb von Rechenzentrums-Infrastruktur, physische Sicherheit, lokale Telekommunikationsinfrastruktur, Serverräume, Energie- oder Klimadienstleistungen.' },
        { category: 'S08 - Berechnung (ohne Cloud)', description: 'Digitale Berechnungs- oder Verarbeitungskapazitäten, nicht Cloud-basiert.' },
        { category: 'S09 - Nicht-Cloud-Datenspeicherung', description: 'Bereitstellung digitaler Datenspeicherplattformen ohne Cloud-Charakter.' },
        { category: 'S10 - Telekommunikationsanbieter', description: 'Betrieb oder Management von Telekommunikationssystemen (außer explizit ausgeschlossene analoge Telefondienste).' },
        { category: 'S11 - Netzinfrastruktur', description: 'Bereitstellung oder Betrieb von Netzwerkkomponenten (Router, Switches, WLAN, Netzwerküberwachung, spezifische Leitungen).' },
        { category: 'S12 - Hardware & physische Geräte als Dienst', description: 'Bereitstellung und Betrieb von Endgerätehardware als Service. Reiner Hardwarekauf ist nicht DORA-relevant.' },
        { category: 'S13 - Softwarelizenzierung (ohne SaaS)', description: 'Lizenzbereitstellung mit zusätzlichen Dienstleistungen wie Konfiguration, Härtung oder Remote-Support. Reiner Lizenzkauf ohne Services ist nicht DORA-relevant.' },
        { category: 'S14 - IKT-Betriebsmanagement', description: 'Technischer Betrieb von Anwendungen oder Infrastruktur (Installation, Wartung, Kapazitätsmanagement, Change-Management, Managed Services).' },
        { category: 'S15 - IKT-Beratung', description: 'IKT-bezogene Fachberatungen, Schulungen oder Notfallmanagement-Unterstützung, sofern digitaler Dienstanteil prägend ist. Restriktiv verwenden.' },
        { category: 'S16 - IKT-Risikomanagement', description: 'Überprüfung oder Unterstützung der IKT-Risikomanagementanforderungen gemäß Art. 6 Abs. 10 DORA. Restriktiv verwenden.' },
        { category: 'S17 - Cloud-Dienste: IaaS', description: 'Infrastructure-as-a-Service: Rechenleistung, Speicher, Netzwerke; Kunde betreibt Software selbst.' },
        { category: 'S18 - Cloud-Dienste: PaaS', description: 'Platform-as-a-Service: Bereitstellung von Entwicklungs-/Betriebsumgebungen zur Ausführung eigener Anwendungen.' },
        { category: 'S19 - Cloud-Dienste: SaaS', description: 'Software-as-a-Service: Cloud-basierte Anwendungen des Dienstleisters, die der Kunde nutzt (z. B. OSPlus, M365).' },
    ];

    private static readonly EXCEL_HEADERS = {
        requirement: 'Anforderung',
        fileName: 'Dateiname',
        passage: 'Fundstelle',
        shortAnswer: 'Initiale KI-Einwertung',
        summary: 'Zusammenfassung',
        overallStatus: 'Gesamtstatus',
        overallSummary: 'Gesamtzusammenfassung',
        keyPassages: 'Key Passages',
    } as const;

    public data = {
        supportText: 'pdf, docx',
        questionConfig: {},
        workflowId: '',
        workflowType: 'documents' as WorkflowType,
        worfkflowInfo: undefined,
        allowCloseWhileLoading: true,
        maxFileSize: 104857600, // 100 MB in bytes
        fileFormats: [
            'application/pdf',
            '.docx',
            'text/plain',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/msword'
        ]
    };
    public ContractStatus = ContractStatus;
    public analysisTypeFormControl = new FormControl('');
    public loaderService = inject(LoaderService);
    public processService = inject(ProcessService);
    contractChecks: PromptSet[] = [];
    public promptSetsLoading = signal(true);

    // Error state management
    public errorDismissed = signal(false);
    public contractErrorDismissed = signal(false);

    public analysisMode = signal<'resume' | 'full'>('resume');
    public showRerunConfirm = signal(false);

    public isAnalyzing = computed(() => this.workflowService.status() === 'loading')

    public analysisProgress = computed(() => this.workflowService.analysisProgress());
    public analysisProgressPercent = computed(() => {
        const p = this.analysisProgress();
        if (!p || p.totalPrompts === 0) return 0;
        return Math.round((p.completedPrompts / p.totalPrompts) * 100);
    });

    public hasProcessedFiles = computed(() => {
        const fileList = this.workflowService.fileList()
        return fileList && fileList.length && fileList.reduce((acc, file) => ['processed', 'uploaded', 'appended'].includes(file.status) && acc, true)
    })

    get analyzeButtonTooltip(): string {
        if (this.isAnalyzing()) return this.translateService.instant('AnalyzeButtonDisabledAnalyzing');
        if (!this.hasProcessedFiles()) return this.translateService.instant('AnalyzeButtonDisabledNoFiles');
        if (!this.analysisTypeFormControl.value) return this.translateService.instant('AnalyzeButtonDisabledNoType');
        return '';
    }

    selectedAnylsisType: string = '';
    @ViewChild(MatAccordion)
    accordion: MatAccordion = new MatAccordion;
    isExpanded = false;
    specifications: Specifications | undefined;
    private hasShownOldSchemaNotification = false;
    // workflowId -> last activeStep value we persisted/confirmed, to avoid repeated PUTs
    private syncedActiveStep = new Map<string, number>();
    public selectedPromptSetSchema: OutputSchema | null = null;
    
    public constructor(public workflowService: WorkflowOverviewService,
        public subscriptionService: SubscriptionOverviewService,
        private snackBar: MatSnackBar, private promptConfigurationService: PromptSetApiService,
        private dialog: MatDialog,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private contentCollectionService: ContentCollectionService,
        private httpClient: HttpClient) {
        this.processService.processResults$
            .pipe(
                takeUntilDestroyed(),
                filter((process) => {
                    return (['completed', 'failed'].includes(process.status));
                })
            )
            .subscribe((processResults: ProcessResponse) => {
                this.processResultActions(processResults);
            });
        this.analysisTypeFormControl.setValue('');
        this.analysisTypeFormControl.valueChanges.subscribe(value => {
            if (value !== null) {
                // Auto-switch to full mode when prompt set changes (new baseline)
                if (this.selectedAnylsisType && this.selectedAnylsisType !== value) {
                    this.analysisMode.set('full');
                }
                this.selectedAnylsisType = value;
                // Fetch schema when prompt set is selected
                this.onAnalysisTypeChange(value);
            }
        });

        effect(() => {
            this.specifications = this.workflowService.specifications();
            this.analysisTypeFormControl.setValue(this.specifications?.promptSetId ?? '');
        });

        // Auto-set mode based on progress state:
        // - completed (100%) or not_started (0%) → Full makes more sense
        // - in_progress (somewhere between) → Resume makes more sense
        effect(() => {
            const progress = this.analysisProgress();
            if (!progress) return;
            if (progress.status === 'completed' || progress.status === 'not_started' ||
                progress.completedPrompts === 0 || progress.completedPrompts >= progress.totalPrompts) {
                untracked(() => this.analysisMode.set('full'));
            } else if (progress.status === 'in_progress') {
                untracked(() => this.analysisMode.set('resume'));
            }
        });

        // Effect to show notification for old schema
        effect(() => {
            const isOldSchema = this.workflowService.isUsingOldSchema();
            const hasResults = this.workflowService.contractResults().length > 0;
            
            if (hasResults) {
                // If we now have new schema results, reset the notification flag
                if (!isOldSchema) {
                    this.hasShownOldSchemaNotification = false;
                }
                // Show notification only for old schema results that haven't been shown yet
                else if (isOldSchema && !this.hasShownOldSchemaNotification) {
                    this.showOldSchemaNotification();
                    this.hasShownOldSchemaNotification = true;
                }
            }
        });

        // Keep the dashboard tile (stored `activeStep`) in sync with the live results.
        // The stored number used to be computed once, with different logic, right after a
        // run completes - which made the overview card disagree with this page and left
        // old contracts stale. Re-derive it from the displayed results whenever they settle.
        effect(() => {
            const workflowId = this.workflowService.workflowId();
            const results = this.workflowService.contractResults();
            const progress = this.analysisProgress();
            if (!workflowId || results.length === 0 || progress?.status === 'in_progress') {
                return;
            }
            const expected = buildActiveStepNumber(countQuestionVerdicts(results));
            untracked(() => {
                if (this.syncedActiveStep.get(workflowId) === expected) {
                    return;
                }
                this.syncedActiveStep.set(workflowId, expected);
                const stored = Number(this.workflowService.workflowData()?.activeStep);
                if (stored !== expected) {
                    this.updateActiveStep(expected);
                }
            });
        });

        // Wait for subscription id to be available, then do a single fetch.
        // This avoids a double-call and keeps the dropdown in a loading state until we have the full list.
        effect(() => {
            const subId = this.subscriptionService.subscriptionId();
            if (subId != null) {
                untracked(() => this.getPromptSets());
            }
        });

        // Resume polling if the page was refreshed while an analysis was in progress.
        // Take the last process in runningProcesses — if it's a running /flow/execution,
        // re-feed it into the polling pipeline so the UI shows the processing state.
        effect(() => {
            const runningProcesses = this.workflowService.runningProcesses();
            if (runningProcesses && runningProcesses.length > 0) {
                const lastProcess = runningProcesses[runningProcesses.length - 1];
                if (lastProcess.status === 'running' && lastProcess.name === '/flow/execution') {
                    untracked(() => {
                        this.processService.startPolling$.next(lastProcess);
                    });
                }
            }
        });
    }

    public ngOnInit(): void {
        // Same as dashboard/home: contract route has no parent that loads subscription data.
        this.subscriptionService.getSubscriptionData$.next();

        this.route.params.pipe(
            map(params => params['id']),
            filter(id => !!id)
        ).subscribe(id => {
            this.workflowService.getWorkflowData$.next(id);
        });
    }

    public ngOnDestroy(): void {
        this.cleanUpAnalysisResult();
        this.cleanUpFiles();
        this.workflowService.stopProgressPolling();
        this.workflowService.resetData();
        this.processService.stopPollingAll$.next();
    }

    public editName(): void {
        const workflowName: string = this.workflowService.workflowName() as string;
        const workflowId: string = this.workflowService.workflowId() as string;

        let dialogRef = this.dialog.open(ContractNameDialogComponent, {
            height: "fit-content",
            width: "max(200px, 400px)",
            data: { name: workflowName },
        });

        dialogRef.afterClosed().subscribe((workflowName: string) => {
            if (workflowName) {
                this.subscriptionService.updateWorflowName(workflowId, workflowName).subscribe();
            }
        });
    }

    updateFileData(event: UploadedFileInformation) {
        this.workflowService.addFileData$.next({
            displayName: event.displayName,
            storedName: event.storedFilename,
            status: 'queued',
            fileExtension: event.process.name.split('.').pop()!,
            uploadedAt: new Date().toISOString(),
        });
        this.loaderService.show();
        this.processService.startPolling$.next(event.process);
        this.cleanUpAnalysisResult();
    }

    public analyseContract(): void {
        const progress = this.analysisProgress();
        if (progress && (progress.status === 'in_progress' || progress.status === 'not_started')) {
            // Analysis is already running — ask the user which mode to use instead of firing immediately
            this.showRerunConfirm.set(true);
            return;
        }
        this.runAnalysis();
    }

    public confirmRerun(mode: 'resume' | 'full'): void {
        this.showRerunConfirm.set(false);
        this.analysisMode.set(mode);
        this.runAnalysis();
    }

    public cancelRerun(): void {
        this.showRerunConfirm.set(false);
    }

    private runAnalysis(): void {
        this.cleanUpAnalysisResult();
        this.workflowService.analyseContract$.next([this.selectedAnylsisType, this.analysisMode()]);
    }

    public setAnalysisMode(mode: 'resume' | 'full'): void {
        this.analysisMode.set(mode);
    }

    public deleteFileData(event: FileData): void {
        this.workflowService.deleteFileData$.next(event);
        this.cleanUpAnalysisResult();
    }

    processResultActions(process: ProcessResponse): void {
        switch (process.name) {
            case '/file/upload':
                if (this.workflowService.workflowId() && process.status !== "failed") {
                    process.status = 'processed';
                    this.workflowService.updateFile$.next(process);
                } else {
                    process.status = 'failed';
                    this.workflowService.updateFile$.next(process);
                }
                break;
            case '/genai':
            case '/flow/execution':
                this.workflowService.updateContractAnalysis$.next(process);
                if (process.status === 'completed') {
                    setTimeout(() => {
                        const results = this.workflowService.contractResults();
                        if (results && results.length > 0) {
                            const doraNumber = this.calculateDoraNumber(results);
                            this.updateActiveStep(doraNumber);
                        }
                    }, 1000);
                }
                break;
            default:
                break;
        }
    }

    private formatExcelDynamicFieldHeader(fieldName: string): string {
        const h = ContractComponent.EXCEL_HEADERS;
        const knownLabels: Record<string, string> = {
            short_answer: h.shortAnswer,
            summary: h.summary,
            passage: h.passage,
        };
        const normalized = fieldName.toLowerCase().replace(/-/g, '_');
        if (knownLabels[normalized]) {
            return knownLabels[normalized];
        }

        return fieldName
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    generateExcel(resultData: Contract[], rawResults?: AnalysisResult[]): ExcelJS.Workbook {
        const workbook = new ExcelJS.Workbook();

        // Summary sheet is first so it is the active tab when the file is opened.
        if (rawResults && rawResults.length > 0) {
            this.addSummarySheet(workbook, rawResults);
        }

        const worksheet = workbook.addWorksheet('Results', {
            pageSetup: {
                fitToPage: true,  // Ensures the worksheet fits to one page
                orientation: 'landscape',
                fitToWidth: 1,
                fitToHeight: 1
            }
        });

        // Determine if we have dynamic schema fields
        const hasDynamicSchema = resultData.some(r => r.result !== undefined);
        let dynamicFields: string[] = [];

        if (hasDynamicSchema) {
            if (this.selectedPromptSetSchema?.fields && this.selectedPromptSetSchema.fields.length > 0) {
                // Schema-driven: use field names from admin prompt config
                dynamicFields = this.selectedPromptSetSchema.fields.map(f => f.field_name);
            } else {
                // Introspect result keys from the first file that has result data
                const firstWithResult = resultData.find(r => r.result && Object.keys(r.result).length > 0);
                if (firstWithResult?.result) {
                    dynamicFields = Object.keys(firstWithResult.result);
                }
            }
        }

        const h = ContractComponent.EXCEL_HEADERS;

        // Build headers based on schema
        let headers: string[];
        if (hasDynamicSchema && dynamicFields.length > 0) {
            headers = ['#', h.requirement, h.fileName, ...dynamicFields.map(f => this.formatExcelDynamicFieldHeader(f)), h.overallStatus, h.overallSummary];
        } else if (hasDynamicSchema) {
            // New schema without explicit dynamic fields — omit Key Passages as
            // analysisSummary.passage is not returned by the backend for new schema.
            headers = ['#', h.requirement, h.fileName, h.summary, h.passage, h.shortAnswer, h.overallStatus, h.overallSummary];
        } else {
            // Old schema — include Key Passages (analysisSummary.passage was available).
            headers = ['#', h.requirement, h.fileName, h.summary, h.passage, h.shortAnswer, h.overallStatus, h.overallSummary, h.keyPassages];
        }
        
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '86bc25' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        const colors = ['FFFFFFFF', 'FFD9D9D9'];
        let currentColorIndex = 0;
        let previousQuestion = '';

        // Add data rows
        resultData.forEach((contract: Contract, index) => {
            if (contract.question !== previousQuestion) {
                currentColorIndex = (currentColorIndex + 1) % colors.length;
                previousQuestion = contract.question;
            }

            const rowColor = colors[currentColorIndex];
            
            // Build row data based on schema
            let rowData: any[];
            if (hasDynamicSchema && dynamicFields.length > 0) {
                // Extract dynamic field values
                const fieldValues = dynamicFields.map(fieldName => {
                    const value = contract.result?.[fieldName] ?? (contract as any)[fieldName] ?? '';
                    return typeof value === 'object' ? JSON.stringify(value) : value;
                });
                
                rowData = [
                    index + 1,
                    contract.question,
                    contract.file,
                    ...fieldValues,
                    contract.overAllStatus || '',
                    contract.overallSummary || ''
                ];
            } else if (hasDynamicSchema) {
                // New schema without explicit dynamic fields — no Key Passages column.
                rowData = [
                    index + 1,
                    contract.question,
                    contract.file,
                    contract.summary || '',
                    contract.passage || '',
                    contract.shortAnswer || '',
                    contract.overAllStatus || '',
                    contract.overallSummary || ''
                ];
            } else {
                // Old schema — include Key Passages.
                rowData = [
                    index + 1,
                    contract.question,
                    contract.file,
                    contract.summary || '',
                    contract.passage || '',
                    contract.shortAnswer || '',
                    contract.overAllStatus || '',
                    contract.overallSummary || '',
                    contract.overallPassages || ''
                ];
            }
            
            const row = worksheet.addRow(rowData);

            row.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: rowColor }
                };
                cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // Color the status cell (position depends on schema)
            const statusCellIndex = hasDynamicSchema && dynamicFields.length > 0 
                ? 4 + dynamicFields.length  // After dynamic fields
                : 7;  // Legacy position
                
            const overAllStatusCell = row.getCell(statusCellIndex);
            switch (contract.overAllStatus) {
                case 'Yes':
                    overAllStatusCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: '86BC25' }
                    };
                    break;
                case 'No':
                    overAllStatusCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'DA291C' }
                    };
                    break;
                case 'ToBeChecked':
                    overAllStatusCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'ED8B00' }
                    };
                    break;
                default:
                    break;
            }
        });

        // Apply wrapText to all cells
        worksheet.columns?.forEach((column) => {
            column.eachCell?.({ includeEmpty: true }, (cell) => {
                cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
            });
        });

        // Set column widths dynamically based on schema
        const columnWidths: Partial<ExcelJS.Column>[] = [
            { width: 5 },   // #
            { width: 60 },  // Requirement
            { width: 30 }   // File Name
        ];
        
        if (hasDynamicSchema && dynamicFields.length > 0) {
            // Add widths for dynamic fields
            dynamicFields.forEach(() => {
                columnWidths.push({ width: 35 });
            });
            columnWidths.push(
                { width: 20 },  // Gesamtstatus
                { width: 50 }   // Gesamtzusammenfassung
            );
        } else if (hasDynamicSchema) {
            // New schema without explicit dynamic fields — no Key Passages column.
            columnWidths.push(
                { width: 40 },  // Zusammenfassung
                { width: 30 },  // Fundstelle
                { width: 20 },  // Initiale KI-Einwertung
                { width: 20 },  // Gesamtstatus
                { width: 50 }   // Gesamtzusammenfassung
            );
        } else {
            // Old schema — include Key Passages column width.
            columnWidths.push(
                { width: 40 },  // Zusammenfassung
                { width: 30 },  // Fundstelle
                { width: 20 },  // Initiale KI-Einwertung
                { width: 20 },  // Gesamtstatus
                { width: 50 },  // Gesamtzusammenfassung
                { width: 40 }   // Key Passages
            );
        }
        
        worksheet.columns = columnWidths;

        // Set zoom to 80%
        worksheet.views = [{ zoomScale: 80 }];

        // Set autofilter - adjust end column based on headers
        const lastColumn = String.fromCharCode(64 + headers.length); // Convert to Excel column letter
        worksheet.autoFilter = {
            from: 'A1',
            to: `${lastColumn}1`
        };

        // Add 4 empty rows before disclaimer
        for (let i = 0; i < 4; i++) {
            worksheet.addRow([]);
        }

        // Add disclaimer
        const disclaimerRow = worksheet.addRow([this.DISCLAIMER_TEXT]);
        
        // Style the disclaimer row
        disclaimerRow.getCell(1).font = { bold: true, italic: true };
        disclaimerRow.getCell(1).alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        disclaimerRow.getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFACD' } // Light yellow background
        };
        disclaimerRow.getCell(1).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
        
        // Merge cells across all columns for the disclaimer
        const lastCol = String.fromCharCode(64 + headers.length);
        worksheet.mergeCells(`A${disclaimerRow.number}:${lastCol}${disclaimerRow.number}`);

        // Add Pivot sheet
        this.addPivotSheet(workbook, resultData);

        return workbook;
    }


    /**
     * DEV-ONLY / hardcoded gate for the temporary IKT-Scanner legend demo feature.
     * See IKT_LEGEND_PROMPT_SET_NAME for context.
     */
    private shouldShowIktLegend(): boolean {
        // `environment.production` is true for both dev and prod builds in this app,
        // so we gate on the backend URL instead (dev backend contains "dev").
        const isDevBackend = !!environment.backendBaseURL?.includes('dev');
        if (!isDevBackend) {
            return false;
        }
        const selectedId = this.analysisTypeFormControl.value;
        if (!selectedId) {
            return false;
        }
        const selectedPromptSet = this.contractChecks.find(ps => ps.prompt_set_id === selectedId);
        return selectedPromptSet?.name === this.IKT_LEGEND_PROMPT_SET_NAME;
    }

    /**
     * DEV-ONLY / hardcoded demo feature for the "Test IKT-Scanner" prompt set.
     * Appends a legend (7 Kriterien + 19 S-Kategorien) below the disclaimer on the given sheet.
     * See IKT_LEGEND_PROMPT_SET_NAME for context — not a long-term solution.
     */
    private addIktLegend(sheet: ExcelJS.Worksheet, lastColLetter: string): void {
        const sectionTitleFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '86bc25' } };
        const tableHeaderFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9D9D9' } };
        const thinBorder: Partial<ExcelJS.Borders> = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };

        const addSectionTitle = (title: string) => {
            sheet.addRow([]);
            const row = sheet.addRow([title]);
            row.getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            row.getCell(1).fill = sectionTitleFill;
            sheet.mergeCells(`A${row.number}:${lastColLetter}${row.number}`);
        };

        // Widen the first (title/category) column by merging A:B, and use C:lastCol
        // for the explanation/description column so the Kriterium/Kategorie text
        // isn't squeezed into the narrow "#" column width.
        const titleColEnd = 'B';
        const descColStart = 'C';

        const addTableHeader = (col1: string, col2: string) => {
            const row = sheet.addRow([col1, undefined, col2]);
            row.getCell(1).font = { bold: true };
            row.getCell(1).fill = tableHeaderFill;
            row.getCell(1).border = thinBorder;
            sheet.mergeCells(`A${row.number}:${titleColEnd}${row.number}`);
            const descCell = row.getCell(3);
            descCell.font = { bold: true };
            descCell.fill = tableHeaderFill;
            descCell.border = thinBorder;
            sheet.mergeCells(`${descColStart}${row.number}:${lastColLetter}${row.number}`);
        };

        const addTableRow = (col1: string, col2: string) => {
            const row = sheet.addRow([col1, undefined, col2]);
            row.getCell(1).alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
            row.getCell(1).border = thinBorder;
            sheet.mergeCells(`A${row.number}:${titleColEnd}${row.number}`);
            const descCell = row.getCell(3);
            descCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
            descCell.border = thinBorder;
            sheet.mergeCells(`${descColStart}${row.number}:${lastColLetter}${row.number}`);
        };

        addSectionTitle('Legende: 7 Kriterien');
        addTableHeader('Kriterium', 'Erklärung');
        this.IKT_KRITERIEN.forEach(({ title, explanation }) => addTableRow(title, explanation));

        addSectionTitle('Legende: 19 S-Kategorien');
        addTableHeader('Kategorie', 'Bezeichnung');
        this.IKT_S_KATEGORIEN.forEach(({ category, description }) => addTableRow(category, description));
    }

    addSummarySheet(workbook: ExcelJS.Workbook, rawResults: AnalysisResult[]): void {
        const sheet = workbook.addWorksheet('Summary', {
            pageSetup: {
                fitToPage: true,
                orientation: 'landscape',
                fitToWidth: 1,
                fitToHeight: 1
            }
        });

        const h = ContractComponent.EXCEL_HEADERS;
        const headers = ['#', h.requirement, h.overallStatus, h.overallSummary];
        const headerRow = sheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '86bc25' } };
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };
        });

        rawResults.forEach((item, index) => {
            const summaryAnswer = item.analysisSummary?.shortAnswer || '';
            const verdict = classifySummaryAnswer(summaryAnswer);
            let status: string;
            switch (verdict) {
                case 'yes': status = 'Yes'; break;
                case 'no': status = 'No'; break;
                default: status = 'ToBeChecked';
            }
            const summary = item.analysisSummary?.summary || '';
            const requirement = item.promptBasic?.name || item.question || 'Unknown Requirement';

            const row = sheet.addRow([index + 1, requirement, status, summary]);

            row.eachCell((cell) => {
                cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            });

            const statusCell = row.getCell(3);
            switch (verdict) {
                case 'yes':
                    statusCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '86BC25' } };
                    break;
                case 'no':
                    statusCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DA291C' } };
                    statusCell.font = { color: { argb: 'FFFFFFFF' } };
                    break;
                default:
                    statusCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ED8B00' } };
                    break;
            }
        });

        sheet.columns = [
            { width: 5 },   // #
            { width: 60 },  // Anforderung
            { width: 20 },  // Gesamtstatus
            { width: 80 },  // Overall Summary
        ];

        sheet.views = [{ zoomScale: 80 }];

        sheet.autoFilter = { from: 'A1', to: 'D1' };

        // Add 4 empty rows before disclaimer
        for (let i = 0; i < 4; i++) {
            sheet.addRow([]);
        }

        const disclaimerRow = sheet.addRow([this.DISCLAIMER_TEXT]);
        disclaimerRow.getCell(1).font = { bold: true, italic: true };
        disclaimerRow.getCell(1).alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        disclaimerRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFACD' } };
        disclaimerRow.getCell(1).border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };
        sheet.mergeCells(`A${disclaimerRow.number}:D${disclaimerRow.number}`);

        if (this.shouldShowIktLegend()) {
            this.addIktLegend(sheet, 'D');
        }
    }


    addPivotSheet(workbook: ExcelJS.Workbook, resultData: Contract[]) {
        // Create a new sheet for the pivot table
        const pivotSheet = workbook.addWorksheet('Pivot');

        // Add header row
        const headers = [ContractComponent.EXCEL_HEADERS.requirement, 'Count of File Name'];
        const headerRow = pivotSheet.addRow(headers);

        // Style the header row
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '86bc25' }
            };
        });

        // Process pivot table data
        const pivotData: { [key: string]: number } = {};
        resultData.forEach((contract) => {
            pivotData[contract.question] = (pivotData[contract.question] || 0) + 1;
        });

        // Add data rows
        Object.entries(pivotData).forEach(([requirement, count]) => {
            const row = pivotSheet.addRow([requirement, count]);
            row.eachCell((cell) => {
                cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
            });
        });

        // Apply column widths
        pivotSheet.columns = [
            { width: 60 },
            { width: 20 }
        ];

        // Set the view to zoom at 80%
        pivotSheet.views = [
            { state: 'normal', zoomScale: 80 }
        ];

        // Set page layout for one-page view with no page breaks
        pivotSheet.pageSetup = {
            fitToPage: true,
            fitToWidth: 1,
            fitToHeight: 1,
            horizontalCentered: true,
            verticalCentered: true
        };

        // Add 4 empty rows before disclaimer
        for (let i = 0; i < 4; i++) {
            pivotSheet.addRow([]);
        }

        // Add disclaimer
        const disclaimerRow = pivotSheet.addRow([this.DISCLAIMER_TEXT]);
        
        // Style the disclaimer row
        disclaimerRow.getCell(1).font = { bold: true, italic: true };
        disclaimerRow.getCell(1).alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        disclaimerRow.getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFACD' } // Light yellow background
        };
        disclaimerRow.getCell(1).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
        
        // Merge cells across both columns for the disclaimer
        pivotSheet.mergeCells(`A${disclaimerRow.number}:B${disclaimerRow.number}`);
    }


    downloadExcel(workbook: ExcelJS.Workbook): void {
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, 'results_from_analysis.xlsx');
        });
    }

    downloadResults(): void {
        const results = this.workflowService.contractFilteredResults();
        if (results) {
            // One row per question × document so File Name and Passage are always populated.
            // The Summary sheet (added inside generateExcel) provides the clean per-question view.
            const resultData: Contract[] = results.flatMap(i => {
                // Derive overAllStatus once per question using the most reliable source:
                // analysisSummary (new schema) first, then file-level aggregation (old schema).
                let overAllStatus = 'ToBeChecked';
                const summaryAnswer = i.analysisSummary?.shortAnswer;
                if (summaryAnswer) {
                    // Word-boundary based classification (see classify-summary-answer.ts).
                    // Substring matching used to misclassify sentences like
                    // "Ja, erfüllt — es liegen keine Mängel vor" as No because
                    // `includes('no')` / `includes('nein')` matched inside other words.
                    switch (classifySummaryAnswer(summaryAnswer)) {
                        case 'yes':
                            overAllStatus = 'Yes';
                            break;
                        case 'no':
                            overAllStatus = 'No';
                            break;
                        default:
                            overAllStatus = 'ToBeChecked';
                    }
                } else {
                    // At least one "yes" -> yes
                    if (atLeastOneValue('Yes', i.files) || atLeastOneValue('Ja', i.files) || atLeastOneValue('erfüllt', i.files)) {
                        overAllStatus = 'Yes';
                    // All "no" -> no
                    } else if (allSameValue('No', i.files) || allSameValue('Nein', i.files) || allSameValue('nicht erfüllt', i.files)) {
                        overAllStatus = 'No';
                    // Mix of "no" and "to be checked" -> to be checked
                    } else {
                        overAllStatus = 'ToBeChecked';
                    }
                }

                return i.files.map(f => ({
                    question: i.promptBasic?.name || 'Unknown Question',
                    ...f,
                    // For new schema, passage/summary/shortAnswer live inside f.result,
                    // not at the top level. Map them explicitly so the legacy Excel
                    // columns (Passage, Summary, Initial AI assessment) are populated.
                    passage: f.result?.['passage'] ?? f.passage ?? '',
                    summary: f.result?.['summary'] ?? f.summary ?? '',
                    shortAnswer: f.result?.['short_answer'] ?? f.shortAnswer ?? '',
                    overAllStatus,
                    overallClassification: i.analysisSummary?.shortAnswer || '',
                    overallSummary: i.analysisSummary?.summary || '',
                    overallPassages: i.analysisSummary?.passage || ''
                }));
            });

            const workbook = this.generateExcel(resultData, results);
            this.downloadExcel(workbook);
        } else {
            this.snackBar.open('No valid results available for download.', 'Close', {
                duration: 5000,
                verticalPosition: 'top',
            });
        }
    }

    private isContractResults(data: Contract[]): data is Contract[] {
        return Array.isArray(data) && data.every(item => {
            return typeof item === 'object' &&
                item.hasOwnProperty('question') &&
                typeof item.question === 'string' &&
                item.hasOwnProperty('file') &&
                typeof item.file === 'string' &&
                item.hasOwnProperty('shortAnswer') &&
                typeof item.shortAnswer === 'string' &&
                item.hasOwnProperty('summary') &&
                typeof item.summary === 'string' &&
                item.hasOwnProperty('passage') &&
                typeof item.passage === 'string';
        });
    }

    filterResults(status: string) {
        this.workflowService.filters.update((currentState: FilterState) => {
            if (status === 'yes' || status === 'no' || status === 'check') {
                const newState = { ...currentState, [status]: !currentState[status] };
                return newState;
            } else {
                return currentState;
            }
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadHandler(event: Event) {
        // this.workflowService.updateWorkflowData$.next();
    }

    private cleanUpAnalysisResult(): void {
        this.workflowService.filters.set({ yes: false, no: false, check: false });
        this.workflowService.componentState.update((state => ({
            ...state,
            // @ts-ignore
            workflowData: {
                ...state.workflowData,
                contractResults: undefined,
                activeStep: 1,
                specifications: state.workflowData?.specifications
                    ? ({ ...state.workflowData.specifications, analysisResult: undefined } as any)
                    : state.workflowData?.specifications
            }
        })))
    }

    private cleanUpFiles(): void {
        this.workflowService.componentState.update((state => ({
            ...state,
            // @ts-ignore
            workflowData: {
                ...state.workflowData,
                files: [],
            }
        })))
    }

    public toggleAccordion() {
        if (this.isExpanded) {
            this.accordion.closeAll();
        } else {
            this.accordion.openAll();
        }
        this.isExpanded = !this.isExpanded;
    }


    getPromptSets() {
        // Resolve subscription id: signal value first, then URL query param as fallback (dashboard navigation passes it there).
        const subscriptionId: string | null =
            this.subscriptionService.subscriptionId()
            ?? this.route.snapshot.queryParamMap.get('subscription_id')
            ?? null;

        const query: PromptSetListQuery = {
            published: true,
            subscription_id: subscriptionId,
        };

        this.promptSetsLoading.set(true);

        // shouldIncludeSubscriptionId=true so listPromptSets merges the URL param when present.
        this.promptConfigurationService.listPromptSets(query, true).subscribe({
            next: (promptSet) => {
                const currentUserSubscriptionId: string | null =
                    this.subscriptionService.subscriptionId()
                    ?? this.route.snapshot.queryParamMap.get('subscription_id')
                    ?? null;

                const filteredPromptSets = promptSet.filter(ps => {
                    const accessLevel = ps.accessLevel || ps.access_level;

                    if (accessLevel === PromptAccessLevel.PUBLIC || accessLevel === 'public') {
                        return true;
                    }

                    if (accessLevel === PromptAccessLevel.SUBSCRIPTION_LEVEL || accessLevel === 'subscription_level') {
                        if (!currentUserSubscriptionId) {
                            return true;
                        }
                        return ps.subscription_id === currentUserSubscriptionId;
                    }

                    // No access_level set — include for backward compatibility.
                    return true;
                });

                this.contractChecks = filteredPromptSets.sort((a, b) => {
                    const dateA = new Date(a.created_at || 0).getTime();
                    const dateB = new Date(b.created_at || 0).getTime();
                    return dateB - dateA;
                });
                this.promptSetsLoading.set(false);
            },
            error: () => this.promptSetsLoading.set(false),
        });
    }

    private showOldSchemaNotification(): void {
        const snackBarRef = this.snackBar.open(
            this.translateService.instant('OldSchemaNotification'),
            this.translateService.instant('ReAnalyzeNow'),
            {
                duration: 10000, // 10 seconds
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['info-snackbar']
            }
        );

        snackBarRef.onAction().subscribe(() => {
            // Trigger re-analysis when user clicks the action button
            if (this.hasProcessedFiles() && this.analysisTypeFormControl.value) {
                this.analyseContract();
            } else {
                this.snackBar.open(
                    this.translateService.instant('ReAnalyzeRequirement'),
                    this.translateService.instant('OK'),
                    { duration: 5000 }
                );
            }
        });
    }

    // Error handling methods
    reloadPage(): void {
        window.location.reload();
    }

    clearAndRestart(): void {
        // Clear all files and reset workflow
        const fileList = this.workflowService.fileList();
        if (fileList && fileList.length > 0) {
            fileList.forEach(file => this.deleteFileData(file));
        }
        this.analysisTypeFormControl.reset();
        this.errorDismissed.set(true);
        
        this.snackBar.open(
            this.translateService.instant('FilesCleared'),
            this.translateService.instant('OK'),
            { duration: 3000 }
        );
    }

    retryAnalysis(): void {
        this.contractErrorDismissed.set(true);
        if (this.hasProcessedFiles() && this.analysisTypeFormControl.value) {
            this.analyseContract();
        } else {
            this.snackBar.open(
                this.translateService.instant('RetryRequirement'),
                this.translateService.instant('OK'),
                { duration: 5000 }
            );
        }
    }

    deleteAllFiles(): void {
        const confirmed = confirm(this.translateService.instant('ConfirmDeleteAllFiles'));
        if (confirmed) {
            const fileList = this.workflowService.fileList();
            if (fileList && fileList.length > 0) {
                fileList.forEach(file => this.deleteFileData(file));
            }
            this.contractErrorDismissed.set(true);
            
            this.snackBar.open(
                this.translateService.instant('AllFilesDeleted'),
                this.translateService.instant('OK'),
                { duration: 3000 }
            );
        }
    }

    contactSupport(): void {
        // Open email client with pre-filled error information
        const subject = encodeURIComponent('DORA Analysis Error Support Request');
        const body = encodeURIComponent(
            `Hello Support Team,\n\n` +
            `I encountered an error while using DORA.\n\n` +
            `Workflow ID: ${this.workflowService.workflowId()}\n` +
            `Timestamp: ${new Date().toISOString()}\n` +
            `Files uploaded: ${this.workflowService.fileList()?.length || 0}\n\n` +
            `Please assist.\n\n` +
            `Best regards`
        );
        window.open(`mailto:support@example.com?subject=${subject}&body=${body}`, '_blank');
    }

    showHelp(): void {
        // Show help information in a snackbar or navigate to help page
        this.snackBar.open(
            this.translateService.instant('HelpInfo'),
            this.translateService.instant('OK'),
            { 
                duration: 10000,
                horizontalPosition: 'center',
                verticalPosition: 'top'
            }
        );
    }

    dismissError(): void {
        this.errorDismissed.set(true);
    }

    dismissContractError(): void {
        this.contractErrorDismissed.set(true);
    }

    getErrorDetails(): string {
        return JSON.stringify({
            timestamp: new Date().toISOString(),
            workflowId: this.workflowService.workflowId(),
            workflowName: this.workflowService.workflowName(),
            fileCount: this.workflowService.fileList()?.length || 0,
            analysisType: this.analysisTypeFormControl.value,
            status: this.workflowService.status()
        }, null, 2);
    }

    /**
     * Resolve schema when prompt set is selected.
     * Uses already-loaded list data from contractChecks to avoid calling
     * get_prompt_set (which performs subscription validation checks and
     * only returns draft data – inaccessible for prompt sets that belong
     * to other subscriptions).
     */
    onAnalysisTypeChange(promptSetId: string): void {
        if (!promptSetId) {
            this.selectedPromptSetSchema = null;
            return;
        }

        const promptSet = this.contractChecks.find(ps => ps.prompt_set_id === promptSetId);
        if (promptSet?.outputSchema && 'fields' in promptSet.outputSchema) {
            this.selectedPromptSetSchema = promptSet.outputSchema as OutputSchema;
        } else {
            this.selectedPromptSetSchema = null;
        }
    }

    /**
     * Calculate the 7-digit DORA number (activeStep) from analysis results.
     * Format: [overall][yes_count][no_count][check_count]
     * Overall: 2=all yes, 3=all no, 4=mixed / to be checked
     *
     * Uses the same per-question classification as the "Analysis results" chips
     * (analysisSummary.shortAnswer) so the dashboard tile always matches this page.
     */
    calculateDoraNumber(results: AnalysisResult[]): number {
        return buildActiveStepNumber(countQuestionVerdicts(results));
    }

    /**
     * Update active step after analysis
     */
    updateActiveStep(doraNumber: number): void {
        const workflowId = this.workflowService.workflowId();
        if (!workflowId) return;
        this.contentCollectionService.updateActiveStep(workflowId, doraNumber).subscribe({
            error: (error) => {
                console.error('Failed to update active step:', error);
            }
        });
    }
}
